import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import useSWR from 'swr';
import {
  mainColors,
  secondaryColors,
  successColors,
  alertColors,
  accentColors
} from '../../theme/themeConfig';
import PartnerPortalApi from '../../api/partnerPortal';

const TableCard = styled(Box)(() => ({
  background: '#fff',
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(15,28,46,.06)',
  overflow: 'hidden'
}));

const StyledHeaderCell = styled(TableCell)(() => ({
  background: '#F4F7FB',
  borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
  color: secondaryColors.SECONDARY,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '.6px',
  padding: '10px 16px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
}));

const StyledRow = styled(TableRow)(() => ({
  '&:hover': { background: mainColors.MAIN_LIGHT },
  '&:last-child td': { borderBottom: 0 }
}));

const getDocStatusConfig = (status: string) => {
  if (status === 'approved')
    return {
      bg: successColors.SUCCESS_6,
      color: successColors.SUCCESS,
      dot: successColors.SUCCESS_7
    };
  if (status === 'pending_review')
    return {
      bg: accentColors.ACCENT_6,
      color: accentColors.ACCENT,
      dot: accentColors.ACCENT
    };
  return {
    bg: alertColors.ALERT_6,
    color: alertColors.ALERT,
    dot: alertColors.ALERT
  };
};

function StatusPill({ label, status }: { label: string; status: string }) {
  const cfg = getDocStatusConfig(status);
  return (
    <Box
      sx={{
        alignItems: 'center',
        background: cfg.bg,
        borderRadius: '20px',
        display: 'inline-flex',
        fontSize: '11px',
        fontWeight: 600,
        gap: '5px',
        px: '9px',
        py: '3px',
        whiteSpace: 'nowrap'
      }}
    >
      <Box
        sx={{
          background: cfg.dot,
          borderRadius: '50%',
          flexShrink: 0,
          height: '6px',
          width: '6px'
        }}
      />
      <span style={{ color: cfg.color }}>{label}</span>
    </Box>
  );
}

const getDocStatusLabel = (status: string) => {
  if (status === 'approved') return 'Approved';
  if (status === 'pending_review') return 'Pending';
  return 'Rejected';
};

const PortalCompliance: React.FC = function PortalCompliance() {
  const { data: compliance } = useSWR(
    'portal-compliance',
    PartnerPortalApi.getCompliance
  );

  const { data: requirements } = useSWR(
    'portal-requirements',
    PartnerPortalApi.getRequirements
  );

  const { data: documents } = useSWR(
    'portal-documents',
    PartnerPortalApi.getDocuments
  );

  if (!compliance || !requirements) return null;

  return (
    <Box>
      <Typography
        sx={{
          color: secondaryColors.SECONDARY,
          fontSize: '22px',
          fontWeight: 600,
          mb: 1
        }}
      >
        Compliance status
      </Typography>
      <Typography
        sx={{ color: secondaryColors.SECONDARY, fontSize: '13px', mb: 3 }}
      >
        Your host company requires certain documents before your account can be
        approved.
      </Typography>

      <Box
        sx={{
          alignItems: 'center',
          background: compliance.compliant
            ? successColors.SUCCESS_6
            : accentColors.ACCENT_6,
          borderRadius: '10px',
          display: 'flex',
          gap: 1.5,
          mb: 3,
          p: 2
        }}
      >
        {compliance.compliant ? (
          <CheckCircleIcon sx={{ color: successColors.SUCCESS }} />
        ) : (
          <ErrorOutlineIcon sx={{ color: accentColors.ACCENT }} />
        )}
        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
          {compliance.compliant
            ? 'All requirements met — your account is compliance-ready.'
            : 'Some requirements are not yet met. See details below.'}
        </Typography>
      </Box>

      {requirements.length === 0 ? (
        <Typography sx={{ color: secondaryColors.SECONDARY, fontSize: '13px' }}>
          No document requirements have been configured by your host company.
        </Typography>
      ) : (
        <TableCard>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledHeaderCell>Requirement</StyledHeaderCell>
                  <StyledHeaderCell>Type</StyledHeaderCell>
                  <StyledHeaderCell>Required</StyledHeaderCell>
                  <StyledHeaderCell>Your document</StyledHeaderCell>
                  <StyledHeaderCell>Status</StyledHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requirements.map((req) => {
                  const doc =
                    documents?.find((d) => d.requirement?.id === req.id) ||
                    documents?.find(
                      (d) =>
                        !d.requirement && d.documentType === req.documentType
                    );
                  return (
                    <StyledRow key={req.id}>
                      <TableCell>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                          {req.name}
                        </Typography>
                        {req.description && (
                          <Typography
                            sx={{
                              color: secondaryColors.SECONDARY,
                              fontSize: '11px'
                            }}
                          >
                            {req.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '12px' }}>
                          {req.documentType}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusPill
                          label={req.isRequired ? 'Required' : 'Optional'}
                          status={
                            req.isRequired ? 'pending_review' : 'approved'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '13px' }}>
                          {doc ? doc.name : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {doc ? (
                          <StatusPill
                            label={getDocStatusLabel(doc.status)}
                            status={doc.status}
                          />
                        ) : (
                          <StatusPill label="Not uploaded" status="missing" />
                        )}
                      </TableCell>
                    </StyledRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TableCard>
      )}
    </Box>
  );
};

export default PortalCompliance;
